import { AppNS } from 'app';
import { API_URL } from 'app/core/config';
import axios, { Canceler } from 'axios';

import { getErrorMessage } from './helpers/errorHandling';

const CancelToken = axios.CancelToken;
let cancel: Canceler;

const URL = 'users.json';
const ONBOARDING_STEP_URL = 'users/onboarding_step.json';
const CLIENT_ONBOARDING_STEP_URL = 'users/client_onboarding_step.json';
const AVATAR_URL = 'users/avatar.json';
const INTERESTS_URL = 'users/interests.json';
const OTHER_NEEDS_URL = 'users/other_needs.json';
const LANGUAGES_URL = 'users/languages.json';
const CLIENT_DETAILS_URL = 'users/details.json';
const EMAIL_AVAILABILITY = 'users/check-email-address-availablility.json';
const SERVICE_ADDRESS_URL = 'users/service_address.json';
const DISTANCE_URL = 'users/service_distance.json';
const CLAIM_ACCOUNT_DETAILS_URL = 'users/claim_account_details.json';

export function registerUser(reqData: AppNS.Params): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiChangeOnboardingStep(reqData: AppNS.Params): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${ONBOARDING_STEP_URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.onboardingStep);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiChangeClientOnboardingStep(reqData: AppNS.Params): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${CLIENT_ONBOARDING_STEP_URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.onboardingStep);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetAvatar(reqData: AppNS.Params): Promise<AppNS.Avatar> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${AVATAR_URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.avatar.versions);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetClaimAccountDetails(reqData: AppNS.Params): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${CLAIM_ACCOUNT_DETAILS_URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetInterests(reqData: AppNS.Params): Promise<{ interests: Array<string>; extraInterests: string }> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${INTERESTS_URL}`, reqData, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.user) {
          resolve({
            interests: data.user.properties.interests,
            extraInterests: data.user.properties.extraInterests,
          });
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetOtherNeeds(reqData: AppNS.Params): Promise<Array<string>> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${OTHER_NEEDS_URL}`, reqData, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.otherNeeds);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetLanguages(reqData: AppNS.Params): Promise<AppNS.Params> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${LANGUAGES_URL}`, reqData, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.languages);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiCheckEmail(params: AppNS.Params): Promise<boolean> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${EMAIL_AVAILABILITY}`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params,
      })
      .then(({ data }) => {
        if (data) {
          resolve(data.emailAvailable);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetClientDetails(reqData: AppNS.Params): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${CLIENT_DETAILS_URL}`, reqData)
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetServiceAddressId(reqData: AppNS.Params): Promise<string> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${SERVICE_ADDRESS_URL}`, reqData, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.serviceAddressId);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiSetServiceDistance(reqData: AppNS.Params): Promise<string> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${DISTANCE_URL}`, reqData, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.user) {
          resolve(data.user.properties.serviceDistanceMi);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetProjectManagers(agencyCompanyId: string): Promise<Array<AppNS.User>> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/users/project-managers.json`, {
        params: { agency_company_id: agencyCompanyId },
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.results) {
          resolve(data.results);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetUsersByEmail(email: string): Promise<Array<AppNS.User>> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/users/get-by-email.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          email,
        },
      })
      .then(({ data }) => {
        if (data.users) {
          resolve(data.users);
        } else {
          reject('Invalid response data');
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function apiGetUsersByName(name: string): Promise<number> {
  // mock response wit setTimeout
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(0);
    }, 1);
  });
}
